// Client for the generate-survey-export edge function: the server-side
// counterpart of the local exportSurveyToExcelTemplate flow, used when the
// property carries an excel_template_url. Raw fetch (not functions.invoke) —
// the download path needs the response blob plus Content-Disposition and the
// X-Export-* headers.

import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/shared/integrations/supabase/client";
import { getFirebaseIdToken } from "@/shared/integrations/firebase/client";

export type EdgeExportResult =
  | { kind: "file"; blob: Blob; filename: string; written: number; mapped: number }
  | { kind: "uploaded"; filename: string; downloadUrl: string; written: number; mapped: number }
  | { kind: "error"; error: string; status: number; message?: string };

export async function generateSurveyExportViaEdge(params: {
  propertyId: string;
  projectId: string;
  destination: "download" | "upload";
  defaultSheet?: string;
  /** The survey open in the editor — a property can have several; omitting exports the latest. */
  surveyId?: string;
}): Promise<EdgeExportResult> {
  const idToken = await getFirebaseIdToken();
  if (!idToken) return { kind: "error", error: "not_signed_in", status: 401 };

  const res = await fetch(`${SUPABASE_URL}/functions/v1/generate-survey-export`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_PUBLISHABLE_KEY,
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(params),
  });

  const contentType = res.headers.get("content-type") ?? "";
  if (!res.ok || contentType.includes("application/json")) {
    const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (res.ok && json.uploaded) {
      return {
        kind: "uploaded",
        filename: String(json.filename ?? ""),
        downloadUrl: String(json.downloadUrl ?? ""),
        written: Number(json.written ?? 0),
        mapped: Number(json.mapped ?? 0),
      };
    }
    return {
      kind: "error",
      error: String(json.error ?? `http_${res.status}`),
      status: res.status,
      message: typeof json.message === "string" ? json.message : undefined,
    };
  }

  const filename =
    res.headers.get("content-disposition")?.match(/filename="([^"]+)"/)?.[1] ?? "survey-export.xlsx";
  return {
    kind: "file",
    blob: await res.blob(),
    filename,
    written: Number(res.headers.get("x-export-written") ?? 0),
    mapped: Number(res.headers.get("x-export-mapped") ?? 0),
  };
}
